import clsx from "clsx";
import { useEffect, useState } from "react";

import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Notification,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Product } from "@transfr-inc/dashboard-components/constants";
import { Button } from "@transfr-inc/dashboard-components/forms";

import { trackFilterEvent } from "@lib/event-trackers";
import { useDateStore } from "@src/stores/zustand/date-store";
import {
  ClassroomFilter,
  DateRangeSelector,
  ModuleFilter,
  OrganizationFilter,
  ProgramFilter,
} from "@components/custom/forms";

import "./chart-filter.modal.scss";

export default ({
  open,
  onCancel,
  userInfo,
  orgCode,
  onApplyFilters,
  classroomEnabled,
  organizationEnabled,
  moduleEnabled,
  programEnabled,
  backdropClassName,
  modalClassName,
  product,
  filterCount,
  onToggleAllTime,
  professionalLearning,
}) => {
  const dateRegex = /^(?:\d{2}\/){2}\d{4}$/;

  const { userId, role } = userInfo || {};
  const [dateRange, setDateRange] = useState();
  const [classrooms, setClassrooms] = useState();
  const [organization, setOrganization] = useState();
  const [module, setModule] = useState();
  const [program, setProgram] = useState();
  const [daysToAdd, setDaysToAdd] = useState();
  const [calendarOpen, setCalendarOpen] = useState();
  const [viewedDates, setViewedDates] = useState({});
  const [notification, setNotification] = useState();

  const setLastDateRange = useDateStore((state) => state.setLastDateRange);
  const setLastDateFilter = useDateStore((state) => state.setLastDateFilter);
  const [modalFilterCount, setModalFilterCount] = useState(filterCount);

  useEffect(() => {
    updateFilterCountHandler();
  }, [classrooms, organization, module, program]);

  const onDateRangeChange = (startDate, endDate, daysToAdd) => {
    const dateRange = { startDate, endDate };
    setDateRange(dateRange);
    setDaysToAdd(daysToAdd);
    // Workaround to set the date range when when loading the modal
    !open && onApplyFilters({ dateRange });
  };

  const onApply = () => {
    setLastDateRange(dateRange);
    setLastDateFilter(daysToAdd);
    daysToAdd > 365 ? onToggleAllTime(true) : onToggleAllTime();
    trackFilterEvent(dateRange?.startDate, dateRange?.endDate);
    onApplyFilters({
      dateRange,
      classrooms,
      organization,
      daysToAdd,
      module,
      program,
    });
  };

  const onCancelChartFilter = () => {
    onCancel();
    setCalendarOpen();
  };

  const onSetCalendarOpen = (open) => {
    setCalendarOpen(open);
  };

  const updateFilterCountHandler = () => {
    let count = 1;
    if (classrooms) {
      count++;
    }
    if (organization) {
      count++;
    }
    if (module) {
      count++;
    }
    if (program) {
      count++;
    }
    setModalFilterCount(count);
  };

  useEffect(() => {
    if (
      dateRegex.test(viewedDates?.startDate) &&
      dateRegex.test(viewedDates?.endDate)
    ) {
      const start = new Date(viewedDates.startDate);
      const end = new Date(viewedDates.endDate);
      if (end < start) {
        setNotification({
          type: NotificationType.error,
          message: "Date formatted incorrectly.",
          id: "calendar-range-error",
        });
      }
      setNotification();
    } else {
      setNotification({
        type: NotificationType.error,
        message: "Date formatted incorrectly.",
        id: "calendar-format-error",
      });
    }
  }, [viewedDates?.startDate, viewedDates?.endDate]);

  return (
    <Modal
      open={open}
      onClose={onCancelChartFilter}
      backdropClassName={backdropClassName}
      modalClassName={clsx("chart-filter-modal", modalClassName)}
    >
      <ModalHeader
        title={
          <div className="modal-title-badge">
            {"Filters"}
            {modalFilterCount > 0 && (
              <div className="badge">{modalFilterCount}</div>
            )}
          </div>
        }
        icon={["fa-regular", "sliders"]}
      ></ModalHeader>
      <ModalBody>
        <div className="filters-container">
          <div className="filter-container filter-date-range">
            {notification && (
              <Notification
                type={notification.type}
                icon={notification.icon}
                tabIndex={-1}
              >
                {notification.message}
              </Notification>
            )}
            <h3>Date Range</h3>
            <DateRangeSelector
              key="date-range-seletor"
              onDateRangeChange={onDateRangeChange}
              showDivider
              setCalendarOpen={onSetCalendarOpen}
              calendarOpen={calendarOpen}
              setViewedDates={setViewedDates}
            />
          </div>
          {organizationEnabled && (
            <div className="filter-container">
              <h3>Organization</h3>
              <OrganizationFilter
                orgCode={orgCode}
                onOrganizationSelected={setOrganization}
                placeholder="All Organizations"
                product={product}
                professionalLearning={professionalLearning}
              />
            </div>
          )}
          {classroomEnabled && (
            <div className="filter-container">
              <h3>Classroom</h3>
              <ClassroomFilter
                onClassroomSelected={setClassrooms}
                orgCode={orgCode}
                placeholder="All Classrooms"
                product={product}
                role={role}
                selectedClassrooms={classrooms}
                userId={userId}
                professionalLearning={professionalLearning}
              />
            </div>
          )}
          {moduleEnabled && product == Product.TS && (
            <div className="filter-container">
              <h3>Module</h3>
              <ModuleFilter
                orgCode={orgCode}
                onModuleSelected={setModule}
                placeholder="All Modules"
                product={product}
                isParentOrg={organizationEnabled}
              />
            </div>
          )}
          {programEnabled && product == Product.PROG && (
            <div className="filter-container">
              <h3>Program</h3>
              <ProgramFilter
                orgCode={orgCode}
                onProgramSelected={setProgram}
                placeholder="All Programs"
                product={product}
                professionalLearning={professionalLearning}
              />
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onCancelChartFilter}>Cancel</Button>
        <Button onClick={onApply} primary disabled={notification}>
          Apply
        </Button>
      </ModalFooter>
    </Modal>
  );
};
